import React, { FC, useEffect } from "react";
import { Select, makeStyles, Field } from "@fluentui/react-components";
import { first } from "lodash";
import { cadchatService, supabaseApi } from "../../../services";
import { logger } from "../../../utils";
import { useAddInStore, useTokenStore } from "../../../hooks";

export interface OrganizationSelectListProps {
  disabled?: boolean;
}

const useStyles = makeStyles({
  section: {
    display: "flex",
    flexDirection: "column",
    padding: "15px",
  },
  select: {
    width: "100%",
  },
});

export const OrganizationSelectList = ({ disabled = false }: OrganizationSelectListProps) => {
  const styles = useStyles();

  // indicates the current users token
  const token = useTokenStore((state) => state.token);

  // indicates the current user is authenticated
  const isAuthenticated = useTokenStore((state) => state.isAuthenticated);

  // indicates the current users list of organizations
  const organizations = useAddInStore((state) => state.organizations);

  // use to set the organization in the state
  const setOrganizations = useAddInStore((state) => state.setOrganizations);

  // indicates the currently selected organization id
  const selectedOrganizationId = useAddInStore((state) => state.selectedOrganizationId);

  // used to set the selected organization id
  const setSelectedOrganizationId = useAddInStore((state) => state.setSelectedOrganizationId);

  useEffect(() => {
    if (!token || !isAuthenticated) return;

    const loadOrganizations = async () => {
      try {
        /** get all organizations */
        const organizations = await supabaseApi.getOrganizations();

        /** filter out organizations without an active subscription */
        const filteredOrganizations =
          await cadchatService.filterOrganizationsWithActiveSubscriptions(organizations);

        /** set the organizations in the state */
        setOrganizations(filteredOrganizations ?? []);

        // if no organization is selected, set the first one as default
        if (!selectedOrganizationId) {
          const id = first(filteredOrganizations)?.id;
          if (id) setSelectedOrganizationId(id);
        }
      } catch (err) {
        logger.error("Error fetching organizations", err);
        setOrganizations([]);
      }
    };

    loadOrganizations();
  }, [token, isAuthenticated, selectedOrganizationId]);

  return (
    <section className={styles.section}>
      <Field
        label={"Organization"}
        id="organization-select"
        hint={"The default organization we should associate meetings with."}
      >
        <Select
          id="organization-select"
          className={styles.select}
          disabled={disabled}
          defaultValue={selectedOrganizationId}
          onChange={(e) => setSelectedOrganizationId(e.target.value)}
        >
          {organizations.map((org) => (
            <option key={org.id} value={org.id}>
              {org.name}
            </option>
          ))}
        </Select>
      </Field>
    </section>
  );
};
